import { z } from 'zod';

/**
 * Phase 3: Discussion threads on a review. Backed by
 * `flow_review_threads` + `flow_review_comments` (migration 049).
 *
 * A thread is either "general" (no anchor) or "step-anchored"
 * (`stepId` set). Comments are append-only with edited_at for
 * tracking. Threads can be resolved by any reviewer on the review.
 */

export const FlowReviewCommentSchema = z.object({
  id: z.string().uuid(),
  threadId: z.string().uuid(),
  authorId: z.string().uuid(),
  authorName: z.string(),
  body: z.string(),
  createdAt: z.string(),
  editedAt: z.string().nullable(),
});
export type FlowReviewComment = z.infer<typeof FlowReviewCommentSchema>;

export const FlowReviewThreadSchema = z.object({
  id: z.string().uuid(),
  reviewId: z.string().uuid(),
  /** Null for general threads, otherwise the step.id from the canvas. */
  stepId: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  resolvedBy: z.string().uuid().nullable(),
  resolvedByName: z.string().nullable(),
  comments: z.array(FlowReviewCommentSchema),
  createdAt: z.string(),
});
export type FlowReviewThread = z.infer<typeof FlowReviewThreadSchema>;

export const CreateThreadBodySchema = z.object({
  stepId: z.string().min(1).max(200).optional(),
  body: z.string().min(1).max(8000),
});
export type CreateThreadBody = z.infer<typeof CreateThreadBodySchema>;

export const ReplyToThreadBodySchema = z.object({
  body: z.string().min(1).max(8000),
});
export type ReplyToThreadBody = z.infer<typeof ReplyToThreadBodySchema>;

export const ResolveThreadBodySchema = z.object({}).strict();
export type ResolveThreadBody = z.infer<typeof ResolveThreadBodySchema>;

export const ListThreadsResponseSchema = z.object({
  threads: z.array(FlowReviewThreadSchema),
});
export type ListThreadsResponse = z.infer<typeof ListThreadsResponseSchema>;

// ---- Inbox / dashboard ----

export const ReviewInboxStateFilterSchema = z.enum(['open', 'merged', 'all']).default('open');
export type ReviewInboxStateFilter = z.infer<typeof ReviewInboxStateFilterSchema>;

export const ReviewInboxBucketSchema = z.enum(['assigned_to_me', 'requested_by_me', 'recent']);
export type ReviewInboxBucket = z.infer<typeof ReviewInboxBucketSchema>;

export const ReviewInboxItemSchema = z.object({
  reviewId: z.string().uuid(),
  flowId: z.string().uuid(),
  flowName: z.string(),
  branchId: z.string().uuid().nullable(),
  branchName: z.string().nullable(),
  title: z.string().nullable(),
  state: z.string(),
  requestedBy: z.string().uuid(),
  requestedByName: z.string(),
  approvalsCount: z.number().int(),
  changesRequestedCount: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
  bucket: ReviewInboxBucketSchema,
});
export type ReviewInboxItem = z.infer<typeof ReviewInboxItemSchema>;

export const ReviewInboxResponseSchema = z.object({
  items: z.array(ReviewInboxItemSchema),
});
export type ReviewInboxResponse = z.infer<typeof ReviewInboxResponseSchema>;
